import {NumberField} from '@wandb/components';
import {Slider} from '@wandb/weave/components';
import React, {type FC, useState} from 'react';

import {useDebouncedValue} from '../../../../../util/useDebouncedValue';
type SettingsSliderInputProps = {
  initialValue: number;
  updateConfigCallback: (value: number) => void; // Should be a useCallback so that the debounce is not recreated on every render
  min?: number;
  max: number;
  'data-test': string;
  'aria-labelledby': string;
};

export const SettingsSliderInput: FC<SettingsSliderInputProps> = ({
  initialValue,
  updateConfigCallback,
  min,
  max,
  'data-test': dataTest,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const {value: debouncedValue, debouncedSetValue} = useDebouncedValue(
    initialValue,
    500,
    updateConfigCallback
  );

  // Local state to control the value of the slider so we don't have to wait for the updateConfig call to propagate
  const [value, setValue] = useState(debouncedValue);

  const onChangeSlider = (value: number[]) => {
    // Slider.Root only takes an array even though we only care about the first value
    const newValue = value[0];
    setValue(newValue);
    debouncedSetValue(newValue);
  };

  const onChangeNumberInput = (value?: number) => {
    if (value != null) {
      const upperClamped = Math.min(value, max);
      const clampedValue =
        min != null ? Math.max(min, upperClamped) : upperClamped;
      setValue(clampedValue);
      debouncedSetValue(clampedValue);
    }
  };

  return (
    <div className="flex items-center">
      <div className="w-full">
        <Slider.Root
          aria-labelledby={ariaLabelledBy}
          min={min}
          max={max}
          step={1}
          value={[value]}
          onValueChange={onChangeSlider}>
          <Slider.Track>
            <Slider.Range className="bg-wb-accent" />
          </Slider.Track>
          <Slider.Thumb />
        </Slider.Root>
      </div>
      <div className="ml-8" data-test={dataTest}>
        <NumberField
          minValue={min}
          maxValue={max}
          step={1}
          value={value}
          onChange={onChangeNumberInput}
        />
      </div>
    </div>
  );
};
